import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, ReplaySubject } from 'rxjs';
import { SelectedSiteService } from '../../selected-site.service';
import { ApiErrorFactory } from '../api-errors';
import { DebugFlags, DebugFlagsResponseJSON, WordPressInstance } from './debugflags';

@Injectable()
export class DebugSiteApiService {
  private readonly headers = new HttpHeaders().set('Content-Type', 'application/json');
  private readonly siteUrlV2 = '/api/v2/site/';
  private siteId: string;
  private debugFlags$$: ReplaySubject<DebugFlags> = new ReplaySubject<DebugFlags>(1);
  private loading$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private http: HttpClient,
    private alertService: SnackbarService,
    private selectedSiteService: SelectedSiteService,
    private apiErrorFactory: ApiErrorFactory,
  ) {
    this.selectedSiteService.getSiteId().subscribe((siteId: string) => (this.siteId = siteId));
  }

  public loadDebugFlags(instance: WordPressInstance): void {
    if (instance === undefined) {
      this.alertService.openErrorSnack('There was an error finding the Debug status of your site');
      return;
    }
    const params = {
      instance: instance,
    };
    const httpParams = new HttpParams({ fromObject: params });
    this.loading$$.next(true);
    this.http
      .get<DebugFlagsResponseJSON>(this.getDebugUrl(this.siteId), {
        params: httpParams,
      })
      .subscribe(
        (resp) => this.handleHttpResponse(resp),
        (err) => this.handleHttpError(err),
      );
  }

  public setDebug(instance: WordPressInstance, debug: boolean): void {
    if (instance === WordPressInstance.staging) {
      const body = {
        instance: instance,
        display: debug,
      };
      this.loading$$.next(true);
      this.http
        .post<DebugFlagsResponseJSON>(this.getDebugUrl(this.siteId), body, {
          headers: this.headers,
        })
        .subscribe(
          (resp) => this.handleHttpResponse(resp),
          (err) => this.handleHttpError(err),
        );
    } else {
      this.alertService.openErrorSnack('Invalid WordPress instance specified: ' + instance);
    }
  }

  public getDebugFlags$(): Observable<DebugFlags> {
    return this.debugFlags$$.asObservable();
  }

  public getLoading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  private getDebugUrl(siteId: string): string {
    return this.siteUrlV2 + siteId + '/debug/';
  }

  private handleHttpResponse(response: DebugFlagsResponseJSON): void {
    this.debugFlags$$.next(DebugFlags.fromJSON(response.data));
    this.loading$$.next(false);
  }

  private handleHttpError(err: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(err);
    this.alertService.openErrorSnack(parsedError.getUserMessage());
    this.loading$$.next(false);
  }
}
